import { useCallback, useMemo } from 'react'

interface UseDataTableSelectionProps<TData> {
  data: TData[]
  getRowId: (row: TData) => string
  selectedIdList: string[]
  onSelectionChange?: (selectedIdList: string[]) => void
}

interface UseDataTableSelectionReturn {
  allRowIdList: string[]
  isAllSelected: boolean
  isIndeterminate: boolean
  handleSelectAll: (isChecked: boolean) => void
  handleSelectRow: (rowId: string, isChecked: boolean) => void
  isRowSelected: (rowId: string) => boolean
}

/**
 * Hook for managing data table row selection state and handlers.
 * Provides functionality for selecting all rows, individual rows, and checking selection state.
 */
export const useDataTableSelection = <TData>({
  data,
  getRowId,
  selectedIdList,
  onSelectionChange,
}: UseDataTableSelectionProps<TData>): UseDataTableSelectionReturn => {
  const allRowIdList = useMemo(() => data.map(getRowId), [data, getRowId])

  const isAllSelected =
    allRowIdList.length > 0 && allRowIdList.every((id) => selectedIdList.includes(id))

  const isIndeterminate = selectedIdList.length > 0 && !isAllSelected

  const handleSelectAll = useCallback(
    (isChecked: boolean): void => {
      const newSelectedIdList = isChecked
        ? [...new Set([...selectedIdList, ...allRowIdList])]
        : selectedIdList.filter((id) => !allRowIdList.includes(id))
      onSelectionChange?.(newSelectedIdList)
    },
    [allRowIdList, onSelectionChange, selectedIdList]
  )

  const handleSelectRow = useCallback(
    (rowId: string, isChecked: boolean): void => {
      const newSelectedIdList = isChecked
        ? [...selectedIdList, rowId]
        : selectedIdList.filter((id) => id !== rowId)
      onSelectionChange?.(newSelectedIdList)
    },
    [onSelectionChange, selectedIdList]
  )

  const isRowSelected = useCallback(
    (rowId: string): boolean => selectedIdList.includes(rowId),
    [selectedIdList]
  )

  return {
    allRowIdList,
    isAllSelected,
    isIndeterminate,
    handleSelectAll,
    handleSelectRow,
    isRowSelected,
  }
}
